import { Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Formik, Form, FieldArray, useFormikContext } from 'formik';
import { useSelector } from '@xstate5/react';
import { Divider, Button, Popover, PopoverButton, CloseButton } from '@crazyegginc/hatch';

import { Modal } from '/src/contexts/modal';
import { useModal } from '/src/hooks';
import { ConditionsPopover } from './ConditionsPopover';
import { AudienceUpsellModal } from './modals/AudienceUpsellModal';
import { Criteria } from './condition-row/Criteria';
import { Comparison } from './condition-row/Comparison';
import { Values } from './condition-row/Values';
import { addCondition, draftToDefinition } from '../utils';
import { ACTIONS } from '../machines/audience-builder';
import { validationSchema } from '../metadata';

import { ReactComponent as PlusIcon } from '@crazyegginc/hatch/dist/images/icon-plus.svg';

const CONDITION_SCROLL_MARGIN = 12;
const CONDITION_ERROR_SCROLL_MARGIN = 48;
const CONDITION_TOOLTIP_SCROLL_MARGIN = 110;

export function AudienceEditorModal({ actorRef, onChange = () => {}, initialScrollCriteria = null }) {
  const modal = useModal();
  const draftSeed = useSelector(actorRef, (state) => state.context.draftSeed);
  const mode = useSelector(actorRef, (state) => state.context.mode);
  const [scrollTarget, setScrollTarget] = useState(() =>
    initialScrollCriteria ? { criteria: initialScrollCriteria, requestId: 0 } : null,
  );

  const requestConditionScroll = useCallback((criteria) => {
    setScrollTarget((target) => ({ criteria, requestId: (target?.requestId ?? 0) + 1 }));
  }, []);

  const clearConditionScroll = useCallback((requestId) => {
    setScrollTarget((target) => (target?.requestId === requestId ? null : target));
  }, []);

  function showAudienceEditor() {
    modal.show(<AudienceEditorModal actorRef={actorRef} onChange={onChange} />);
  }

  function showAudienceUpsell(draft, UpsellModal = AudienceUpsellModal) {
    actorRef.send({ type: ACTIONS.SAVE_DRAFT, draft });
    modal.show(<UpsellModal onBack={showAudienceEditor} />);
  }

  return (
    <Modal
      dialogClassName="!max-h-[80dvh] !w-[800px] !overflow-hidden !px-[30px] !pt-5"
      onDismiss={() => actorRef.send({ type: ACTIONS.CANCEL_EDIT })}
      disableOutsideClick={true}
    >
      <Formik
        validationSchema={validationSchema}
        initialValues={draftSeed ?? { conditions: [] }}
        onSubmit={(values) => {
          const definition = draftToDefinition(values);

          actorRef.send({ type: ACTIONS.APPLY_DRAFT, draft: values });
          onChange(definition);
          modal.close();
        }}
      >
        {() => (
          <Form className="flex min-h-0 flex-1 flex-col">
            <FieldArray name="conditions">
              {(actions) => (
                <>
                  <div className="shrink-0">
                    <h2 className="text-header-1 mb-[20px] leading-tight">Target Your Visitors</h2>
                    <Divider dashed className="!w-auto -mx-7" />
                  </div>
                  <ConditionRows
                    actions={actions}
                    mode={mode}
                    onConditionSelected={requestConditionScroll}
                    onScrollTargetHandled={clearConditionScroll}
                    onUpsellClick={showAudienceUpsell}
                    scrollTarget={scrollTarget}
                  />
                  <div className="shrink-0">
                    <AddConditionButton
                      mode={mode}
                      onConditionSelected={requestConditionScroll}
                      onUpsellClick={showAudienceUpsell}
                    />
                    <Modal.Actions className="!mt-[30px]">
                      <Button variant="primary" type="submit" size="lg">
                        Apply
                      </Button>
                      <Button
                        variant="cancel"
                        size="lg"
                        onClick={() => {
                          actorRef.send({ type: ACTIONS.CANCEL_EDIT });
                          modal.close();
                        }}
                      >
                        Cancel
                      </Button>
                    </Modal.Actions>
                  </div>
                </>
              )}
            </FieldArray>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

function AddConditionButton({ mode, onConditionSelected, onUpsellClick }) {
  const { values, setFieldValue } = useFormikContext();

  function onClick(newCriteria) {
    addCondition(null, newCriteria, values, setFieldValue);
    onConditionSelected?.(newCriteria);
  }

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <div className="mr-2.5 mt-3.75">
            <PopoverButton
              as={Button}
              variant="secondary"
              className={classNames('!relative', { '!border-dodger-blue-500': open })}
            >
              <PlusIcon className="mr-2 h-2.5 w-2.5 fill-current" />
              Add Condition
            </PopoverButton>
          </div>
          <ConditionsPopover
            mode={mode}
            onClick={onClick}
            onUpsellClick={(UpsellModal) => onUpsellClick(values, UpsellModal)}
            anchor="top start"
          />
        </>
      )}
    </Popover>
  );
}

function ConditionRows({ actions, mode, onConditionSelected, onScrollTargetHandled, onUpsellClick, scrollTarget }) {
  const { errors, isSubmitting, isValidating, submitCount, values } = useFormikContext();
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const conditionRefs = useRef(new Map());
  const handledErrorSubmitCountRef = useRef(0);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const updateHasScrollbar = useCallback(() => {
    const node = scrollRef.current;
    const nextHasScrollbar = Boolean(node && node.scrollHeight > node.clientHeight + 1);

    setHasScrollbar((currentHasScrollbar) =>
      currentHasScrollbar === nextHasScrollbar ? currentHasScrollbar : nextHasScrollbar,
    );
  }, []);

  useLayoutEffect(() => {
    updateHasScrollbar();

    const scrollNode = scrollRef.current;
    const contentNode = contentRef.current;

    if (!scrollNode || typeof window.ResizeObserver !== 'function') {
      window.addEventListener('resize', updateHasScrollbar);

      return () => window.removeEventListener('resize', updateHasScrollbar);
    }

    const resizeObserver = new window.ResizeObserver(updateHasScrollbar);
    resizeObserver.observe(scrollNode);

    if (contentNode) {
      resizeObserver.observe(contentNode);
    }

    return () => resizeObserver.disconnect();
  }, [updateHasScrollbar]);

  // scroll to newly added (or already existing) criteria
  useLayoutEffect(() => {
    if (!scrollTarget?.criteria) {
      return;
    }

    const condition = values.conditions.find(({ criteria }) => criteria === scrollTarget.criteria);
    const scrollNode = scrollRef.current;
    const conditionNode = condition ? conditionRefs.current.get(condition.criteria) : null;

    if (!condition || !scrollNode || !conditionNode) {
      return;
    }

    const targetNode = conditionNode.querySelector('[data-audience-scroll-target="true"]') ?? conditionNode;

    scrollElementIntoViewIfNeeded(scrollNode, targetNode, {
      bottomMargin: hasConditionTooltipMarker(condition) ? CONDITION_TOOLTIP_SCROLL_MARGIN : CONDITION_SCROLL_MARGIN,
    });
    onScrollTargetHandled?.(scrollTarget.requestId);
  }, [onScrollTargetHandled, scrollTarget, values.conditions]);

  // scroll to first error on submit
  useLayoutEffect(() => {
    if (submitCount === 0 || handledErrorSubmitCountRef.current === submitCount || isSubmitting || isValidating) {
      return;
    }

    const firstError = getFirstConditionError(errors.conditions);
    const scrollNode = scrollRef.current;

    if (!firstError || !scrollNode) {
      return;
    }

    const condition = values.conditions[firstError.conditionIndex];
    const conditionNode = condition ? conditionRefs.current.get(condition.criteria) : null;

    if (!conditionNode) {
      return;
    }

    const targetNode =
      conditionNode.querySelector(`[data-audience-row-index="${firstError.rowIndex}"]`) ?? conditionNode;

    scrollElementIntoViewIfNeeded(scrollNode, targetNode, {
      bottomMargin: CONDITION_ERROR_SCROLL_MARGIN,
    });
    handledErrorSubmitCountRef.current = submitCount;
  }, [errors.conditions, isSubmitting, isValidating, submitCount, values.conditions]);

  return (
    <div
      ref={scrollRef}
      className={classNames(
        'mt-[15px] text-body-2 min-h-0 flex-1 overflow-y-auto -mx-[30px] pl-[30px]',
        'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-cadet-blue-500 scrollbar-thumb-rounded',
        {
          'pr-[30px]': !hasScrollbar,
          'pr-[20px]': hasScrollbar,
        },
      )}
    >
      <div ref={contentRef}>
        <div className="mt-[15px] text-header-4 mb-3.75 ">Include visits that match the following:</div>
        {values.conditions.map((condition, conditionIndex) => (
          <Fragment key={condition.criteria}>
            <div
              ref={(node) => {
                if (node) {
                  conditionRefs.current.set(condition.criteria, node);
                } else {
                  conditionRefs.current.delete(condition.criteria);
                }
              }}
              className="bg-white-lilac-500 w-full rounded-lg relative"
              data-testid="audience-condition-container"
            >
              <div>
                <FieldArray name={`conditions[${conditionIndex}].rows`}>
                  {(rowActions) => (
                    <>
                      {condition.rows.map((row, innerIndex) => {
                        const isScrollTarget =
                          rowHasTooltipMarker(row) || (condition.tooltip === 'criteria' && innerIndex === 0);

                        return (
                          <div
                            className={classNames(
                              'min-h-[55px] w-full flex pl-2.5 pr-10 py-2.5',
                              'border-b border-dashed border-mystic-500 last:border-b-0',
                            )}
                            data-audience-scroll-target={isScrollTarget ? 'true' : undefined}
                            data-audience-row-index={innerIndex}
                            key={row.id}
                          >
                            <FieldArray name={`conditions[${conditionIndex}].rows[${innerIndex}].values`}>
                              {(valueActions) => (
                                <>
                                  <Criteria
                                    index={conditionIndex}
                                    innerIndex={innerIndex}
                                    mode={mode}
                                    onConditionSelected={onConditionSelected}
                                    onUpsellClick={onUpsellClick}
                                  />
                                  <Comparison index={conditionIndex} innerIndex={innerIndex} />
                                  <div className="flex flex-wrap">
                                    <Values
                                      index={conditionIndex}
                                      innerIndex={innerIndex}
                                      mode={mode}
                                      rowActions={rowActions}
                                      valueActions={valueActions}
                                    />
                                  </div>
                                </>
                              )}
                            </FieldArray>
                          </div>
                        );
                      })}
                    </>
                  )}
                </FieldArray>
              </div>
              {values.conditions.length > 1 ? (
                <CloseButton
                  onClick={() => actions.remove(conditionIndex)}
                  label="remove condition"
                  buttonClass="!top-[21px] group"
                  iconClass="group-hover:text-radical-red-500"
                />
              ) : null}
            </div>
            <div className="text-header-4 text-lynch-500 my-1.25 last:hidden">and</div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function scrollElementIntoViewIfNeeded(scrollNode, targetNode, { bottomMargin = CONDITION_SCROLL_MARGIN } = {}) {
  const scrollRect = scrollNode.getBoundingClientRect();
  const targetRect = targetNode.getBoundingClientRect();
  const topMargin = CONDITION_SCROLL_MARGIN;
  const isVisible =
    targetRect.top >= scrollRect.top + topMargin && targetRect.bottom <= scrollRect.bottom - bottomMargin;

  if (isVisible) {
    return;
  }

  let nextScrollTop = scrollNode.scrollTop;

  if (targetRect.top < scrollRect.top + topMargin) {
    nextScrollTop += targetRect.top - scrollRect.top - topMargin;
  } else if (targetRect.bottom > scrollRect.bottom - bottomMargin) {
    nextScrollTop += targetRect.bottom - scrollRect.bottom + bottomMargin;
  }

  if (typeof scrollNode.scrollTo === 'function') {
    scrollNode.scrollTo({ top: Math.max(0, nextScrollTop), behavior: 'smooth' });
  } else {
    scrollNode.scrollTop = Math.max(0, nextScrollTop);
  }
}

function hasConditionTooltipMarker(condition) {
  return Boolean(condition.tooltip || condition.rows.some(rowHasTooltipMarker));
}

function rowHasTooltipMarker(row) {
  return Boolean(row.tooltip || (row.valueTooltips ?? []).length > 0);
}

function getFirstConditionError(conditionErrors) {
  if (!conditionErrors) {
    return null;
  }

  if (typeof conditionErrors === 'string') {
    return { conditionIndex: 0, rowIndex: 0 };
  }

  if (!Array.isArray(conditionErrors)) {
    return null;
  }

  for (const [conditionIndex, conditionError] of conditionErrors.entries()) {
    if (!conditionError) {
      continue;
    }

    if (typeof conditionError === 'string' || conditionError.criteria) {
      return { conditionIndex, rowIndex: 0 };
    }

    const rowIndex = getFirstRowErrorIndex(conditionError.rows);

    if (rowIndex != null) {
      return { conditionIndex, rowIndex };
    }
  }

  return null;
}

function getFirstRowErrorIndex(rowErrors) {
  if (!rowErrors) {
    return null;
  }

  if (typeof rowErrors === 'string') {
    return 0;
  }

  if (!Array.isArray(rowErrors)) {
    return null;
  }

  const rowIndex = rowErrors.findIndex(Boolean);

  return rowIndex === -1 ? null : rowIndex;
}
